import { ClusterJewelTierValue, TierThresholdValue, TierValue, UniquesTierValue } from '../models/item-data-models';

export type TierThreshold = TierValue | UniquesTierValue | ClusterJewelTierValue;

const FRACTION_TOLERANCE = 1e-9;
const FRACTION_STRING_PATTERN = /^(\d+(?:[.,]\d+)?)\/(\d+(?:[.,]\d+)?)$/;

function parseDecimalPart(s: string): number | null {
  const normalized = s.trim().replace(',', '.');
  if (!normalized || !/^-?\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  const n = parseFloat(normalized);
  return isNaN(n) ? null : n;
}

export function isTierThresholdFraction(value: TierThresholdValue): boolean {
  return typeof value === 'string' && value.includes('/');
}

export function parseTierThresholdValue(value: TierThresholdValue): number | null {
  if (typeof value === 'number') {
    return isNaN(value) ? null : value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('/')) {
    const match = trimmed.match(FRACTION_STRING_PATTERN);
    if (!match) {
      return null;
    }
    const numerator = parseDecimalPart(match[1]);
    const denominator = parseDecimalPart(match[2]);
    if (numerator === null || denominator === null || denominator === 0) {
      return null;
    }
    return numerator / denominator;
  }

  return parseDecimalPart(trimmed);
}

export function isValidTierThresholdValue(value: TierThresholdValue): boolean {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    if (trimmed.includes('/')) {
      if (!FRACTION_STRING_PATTERN.test(trimmed)) {
        return false;
      }
    }
  }

  const effective = parseTierThresholdValue(value);
  return effective !== null && effective >= 0;
}

export function normalizeTierThresholdValue(input: string): TierThresholdValue | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.includes('/')) {
    const match = trimmed.match(FRACTION_STRING_PATTERN);
    if (!match) {
      return null;
    }
    const numerator = parseDecimalPart(match[1]);
    const denominator = parseDecimalPart(match[2]);
    if (numerator === null || denominator === null || denominator === 0 || numerator < 0 || denominator < 0) {
      return null;
    }
    return `${match[1].replace(',', '.')}/${match[2].replace(',', '.')}`;
  }

  const parsed = parseDecimalPart(trimmed);
  if (parsed === null || parsed < 0) {
    return null;
  }
  return parsed;
}

export function tierThresholdValueToInput(value: TierThresholdValue): string {
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
}

export function getEffectiveTierThresholdValue(threshold: TierThreshold): number {
  return parseTierThresholdValue(threshold.value) ?? 0;
}

export function formatTierThresholdValue(threshold: TierThreshold): string {
  if (isTierThresholdFraction(threshold.value)) {
    return threshold.value as string;
  }

  const value = typeof threshold.value === 'number'
    ? threshold.value
    : parseTierThresholdValue(threshold.value)!;

  if (value > 0 && value < 1) {
    const denominator = Math.round(1 / value);
    if (denominator > 1 && Math.abs(1 / denominator - value) < FRACTION_TOLERANCE) {
      return `1/${denominator}`;
    }
  }

  return String(value);
}
